import React, { useState, useEffect } from 'react';
import { storeDashboardApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { MultiImageUpload } from '@/components/shared/ImageUpload';
import { Pencil, Eye, Loader2, CheckCircle, Trash2, Image as ImageIcon, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { useNavCategories } from '@/context/NavCategoriesContext';

export const StoreProductDialog = ({ product, token, onSuccess }) => {
  const { categories: navCategories, getSubcategoriesForParent, findSubcategoryLabel } = useNavCategories();
  const [loading, setLoading] = useState(false);
  const [articleNumber, setArticleNumber] = useState("");
  const [finfoLoading, setFinfoLoading] = useState(false);
  const [finfoDocuments, setFinfoDocuments] = useState([]);
  const [viewMode, setViewMode] = useState('edit');
  const [requiresShippingQuote, setRequiresShippingQuote] = useState(product?.requires_shipping_quote || false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [formData, setFormData] = useState({
    title: '', description: '', price: '', original_price: '',
    category_id: '', subcategory_id: '', stock: 0, images: '',
    weight_kg: '', length_cm: '', width_cm: '', height_cm: '', condition: 'new'
  });

  useEffect(() => {
    if (product) {
      setFormData({
        title: product.title || '', description: product.description || '',
        price: product.price || '', original_price: product.original_price || '',
        category_id: product.category_id || '',
        subcategory_id: product.subcategory_id || '',
        stock: product.stock || 0,
        images: product.images?.join(', ') || '',
        weight_kg: product.weight_kg || '', length_cm: product.length_cm || '',
        width_cm: product.width_cm || '', height_cm: product.height_cm || '',
        condition: product.condition || 'new'
      });
      setUploadedImages(product.images || []);
      setRequiresShippingQuote(product.requires_shipping_quote || false);
    } else {
      setFormData({ title: '', description: '', price: '', original_price: '', category_id: '', subcategory_id: '', stock: 0, images: '', weight_kg: '', length_cm: '', width_cm: '', height_cm: '', condition: 'new' });
      setUploadedImages([]);
      setRequiresShippingQuote(false);
    }
    setViewMode('edit');
  }, [product]);

  useEffect(() => {
    if (uploadedImages.length > 0) {
        setFormData(prev => ({ ...prev, images: uploadedImages.join(', ') }));
    }
  }, [uploadedImages]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const subs = getSubcategoriesForParent(formData.category_id);
    if (subs.length > 0 && !formData.subcategory_id) {
      toast.error('Välj underkategori');
      return;
    }
    setLoading(true);

    try {
      const payload = {
        title: formData.title, description: formData.description,
        price: parseFloat(formData.price), category_id: formData.category_id,
        subcategory_id: formData.subcategory_id || null,
        stock: parseInt(formData.stock),
        images: uploadedImages.length > 0 ? uploadedImages : formData.images.split(',').map(s => s.trim()).filter(Boolean),
        specifications: {},
        shipping_method: 'weight_dimensions', shipping_class: null,
        weight_kg: parseFloat(formData.weight_kg) || null,
        length_cm: parseFloat(formData.length_cm) || null,
        width_cm: parseFloat(formData.width_cm) || null,
        height_cm: parseFloat(formData.height_cm) || null,
        requires_shipping_quote: requiresShippingQuote,
        documents: finfoDocuments,
        condition: formData.condition
      };

      if (formData.original_price) {
        payload.original_price = parseFloat(formData.original_price);
      } else {
        payload.original_price = null;
      }

      if (product) {
        await storeDashboardApi.updateProduct(product.id, payload);
        toast.success('Produkten har uppdaterats');
      } else {
        await storeDashboardApi.createProduct(payload);
        toast.success('Produkten har lagts till');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Kunde inte spara produkten');
    } finally {
      setLoading(false);
    }
  };

  const lookupFinfo = async () => {
    if (!articleNumber) return;
    setFinfoLoading(true);
    try {
      const res = await fetch(`/api/finfo/article/${articleNumber}`);
      const data = await res.json();
      if (data.found) {
        setUploadedImages(data.article.images || []);
        setFinfoDocuments(data.article.documents || []);
        setFormData(prev => ({
          ...prev,
          title: data.article.name || prev.title,
          description: data.article.description || prev.description,
          price: data.article.price || prev.price,
          weight_kg: data.article.weight || prev.weight_kg,
        }));
        toast.success("Produktinfo hämtad från Finfo!");
      } else {
        toast.error("Artikeln hittades inte i Finfo");
      }
    } catch (e) {
      toast.error("Kunde inte hämta från Finfo");
    } finally {
      setFinfoLoading(false);
    }
  };

  const discountPreview = formData.original_price && formData.price
    ? { amount: (parseFloat(formData.original_price) - parseFloat(formData.price)).toFixed(0), percent: Math.round(((parseFloat(formData.original_price) - parseFloat(formData.price)) / parseFloat(formData.original_price)) * 100) }
    : null;

  const mainCat = navCategories.find((c) => c.id === formData.category_id);
  const categoryName = [
    mainCat?.name,
    findSubcategoryLabel(formData.category_id, formData.subcategory_id),
  ].filter(Boolean).join(' · ') || 'Kategori';
  const previewImages = uploadedImages.length > 0 ? uploadedImages : formData.images.split(',').map(s => s.trim()).filter(Boolean);

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{product ? 'Redigera annons' : 'Lägg till ny annons'}</DialogTitle>
        <div className="flex gap-2 mt-4">
          <button type="button" onClick={() => setViewMode('edit')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'edit' ? 'bg-[#003B73] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            <Pencil className="h-4 w-4 inline-block mr-2" />Redigera
          </button>
          <button type="button" onClick={() => setViewMode('preview')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'preview' ? 'bg-[#003B73] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            <Eye className="h-4 w-4 inline-block mr-2" />Förhandsgranska
          </button>
        </div>
      </DialogHeader>

      {viewMode === 'preview' ? (
        <div className="py-4">
          <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
            <div className="aspect-square bg-gray-100 relative">
              {previewImages.length > 0 ? (
                <img src={previewImages[0]} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400"><ImageIcon className="h-16 w-16" /></div>
              )}
              {discountPreview && discountPreview.percent > 0 && (
                <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">-{discountPreview.percent}%</div>
              )}
            </div>
            <div className="p-4">
              <p className="text-xs text-gray-500 mb-1">{categoryName}</p>
              <h3 className="font-bold text-lg text-[#0F172A] mb-2">{formData.title || 'Produkttitel'}</h3>
              <p className="text-sm text-gray-600 mb-4 line-clamp-3">{formData.description || 'Produktbeskrivning...'}</p>
              <div className="mb-3">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                  formData.condition === 'new'
                    ? 'bg-emerald-100 text-emerald-700'
                    : formData.condition === 'demo'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-rose-100 text-rose-700'
                }`}>
                  {formData.condition === 'new' ? 'Ny vara' : formData.condition === 'demo' ? 'Demoex' : 'Defekt'}
                </span>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-2xl font-black text-[#FF8A00]">{formData.price || '0'} kr</span>
                {formData.original_price && <span className="text-sm text-gray-400 line-through">{formData.original_price} kr</span>}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className={formData.stock > 0 ? 'text-green-600' : 'text-red-500'}>{formData.stock > 0 ? `${formData.stock} i lager` : 'Slutsåld'}</span>
              </div>
              {requiresShippingQuote && (
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">Tung/skrymmande vara - kontakta för fraktförfrågan</div>
              )}
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 mt-4">Så här kommer annonsen att se ut för kunderna</p>
          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" variant="outline" onClick={() => setViewMode('edit')}>Tillbaka till redigering</Button>
            <Button type="button" disabled={loading} className="bg-[#FF8A00] hover:bg-[#e67c00]" onClick={handleSubmit}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              {product ? 'Spara ändringar' : 'Publicera annons'}
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <Label className="text-blue-800 font-semibold">Hämta från Finfo (artikelnummer)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={articleNumber} onChange={(e) => setArticleNumber(e.target.value)} placeholder="Skriv artikelnummer..." className="bg-white" />
              <Button type="button" onClick={lookupFinfo} disabled={finfoLoading} className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap">
                {finfoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Hämta info"}
              </Button>
            </div>
            <p className="text-xs text-blue-600 mt-1">Fyll i automatiskt från Finfos artikeldatabas</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label htmlFor="title">Titel *</Label>
              <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="description">Beskrivning *</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required className="mt-1" rows={3} />
            </div>
            <div>
              <Label htmlFor="original_price">Ordinarie pris (kr)</Label>
              <Input id="original_price" type="number" step="0.01" value={formData.original_price} onChange={(e) => setFormData({ ...formData, original_price: e.target.value })} placeholder="Visas överstruket" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="price" className="text-red-600 font-semibold">Fyndpris (kr) *</Label>
              <Input id="price" type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required className="mt-1 border-red-300 focus:border-red-500" />
              {discountPreview && discountPreview.percent > 0 && (
                <p className="text-sm text-green-600 mt-1">Du sparar kunden {discountPreview.amount} kr ({discountPreview.percent}% rabatt)</p>
              )}
            </div>
            <div>
              <Label htmlFor="stock">Lagersaldo *</Label>
              <Input id="stock" type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} required className="mt-1" />
            </div>
            <div>
              <Label htmlFor="category_id">Huvudkategori *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value, subcategory_id: '' })}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder="Välj huvudkategori" /></SelectTrigger>
                <SelectContent>
                  {navCategories.map((cat) => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="subcategory_id">Underkategori *</Label>
              <Select
                value={formData.subcategory_id || undefined}
                onValueChange={(value) => setFormData({ ...formData, subcategory_id: value })}
                disabled={!formData.category_id || getSubcategoriesForParent(formData.category_id).length === 0}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={formData.category_id ? 'Välj underkategori' : 'Välj huvudkategori först'} />
                </SelectTrigger>
                <SelectContent>
                  {getSubcategoriesForParent(formData.category_id).map((sub) => (
                    <SelectItem key={sub.slug} value={sub.slug}>{sub.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Samma underkategorier som i Kategorier-menyn – annonsen visas när kunder filtrerar där.</p>
            </div>
            <div>
              <Label htmlFor="condition">Skick *</Label>
              <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Välj skick" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Ny vara</SelectItem>
                  <SelectItem value="demo">Demoex</SelectItem>
                  <SelectItem value="defect">Defekt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="sm:col-span-2 border-t pt-4 mt-2">
              <Label className="font-semibold text-[#003B73] flex items-center gap-2 mb-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg> Dokument (PDF)</Label>
              {finfoDocuments.length > 0 && (
                <div className="mb-3 flex flex-col gap-1">
                  {finfoDocuments.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded text-sm">
                      <span className="text-blue-800">{doc.label}</span>
                      <button type="button" onClick={() => setFinfoDocuments(finfoDocuments.filter((_,j) => j !== i))} className="text-red-400 hover:text-red-600 ml-2">✕</button>
                    </div>
                  ))}
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors">
                <input type="file" accept=".pdf" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    const res = await fetch("/api/upload/document", { method: "POST", body: fd });
                    const data = await res.json();
                    setFinfoDocuments(prev => [...prev, { url: data.url, type: "manual", label: file.name.replace(".pdf","") }]);
                    toast.success("Dokument uppladdat!");
                  } catch { toast.error("Kunde inte ladda upp dokument"); }
                }} />
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                <span className="text-sm text-gray-500">Klicka för att ladda upp PDF</span>
              </label>
            </div>
            <div className="sm:col-span-2 border-t pt-4 mt-2">
              <Label className="font-semibold text-[#003B73] flex items-center gap-2 mb-3"><ImageIcon className="h-4 w-4" /> Produktbilder</Label>
              {uploadedImages.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Nuvarande bilder (klicka för att ta bort):</p>
                  <div className="flex flex-wrap gap-2">
                    {uploadedImages.map((img, index) => (
                      <div key={index} className="relative group cursor-pointer" onClick={() => setUploadedImages(uploadedImages.filter((_, i) => i !== index))}>
                        <img src={img} alt="" className="w-20 h-20 object-cover rounded border" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center"><Trash2 className="h-5 w-5 text-white" /></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <MultiImageUpload images={uploadedImages} onImagesChange={setUploadedImages} maxImages={5} folder="products" />
              <div className="mt-4">
                <Label htmlFor="images" className="text-sm text-gray-600">Eller klistra in bild-URL:er:</Label>
                <Input id="images" value={formData.images} onChange={(e) => setFormData({ ...formData, images: e.target.value })} placeholder="https://example.com/image1.jpg, https://..." className="mt-1" />
                <p className="text-xs text-gray-400 mt-1">Separera flera URL:er med komma</p>
              </div>
            </div>
            
            <div className="sm:col-span-2 border-t pt-4 mt-2">
              <Label className="font-semibold text-[#003B73] flex items-center gap-2 mb-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg> Dokument (PDF)</Label>
              {finfoDocuments.length > 0 && (
                <div className="mb-3 flex flex-col gap-1">
                  {finfoDocuments.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded text-sm">
                      <span className="text-blue-800">{doc.label}</span>
                      <button type="button" onClick={() => setFinfoDocuments(finfoDocuments.filter((_,j) => j !== i))} className="text-red-400 hover:text-red-600 ml-2">✕</button>
                    </div>
                  ))}
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors">
                <input type="file" accept=".pdf" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    const res = await fetch("/api/upload/document", { method: "POST", body: fd });
                    const data = await res.json();
                    setFinfoDocuments(prev => [...prev, { url: data.url, type: "manual", label: file.name.replace(".pdf","") }]);
                    toast.success("Dokument uppladdat!");
                  } catch { toast.error("Kunde inte ladda upp dokument"); }
                }} />
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                <span className="text-sm text-gray-500">Klicka för att ladda upp PDF</span>
              </label>
            </div>
            <div className="sm:col-span-2 border-t pt-4 mt-2">
              <Label className="font-semibold text-[#003B73] flex items-center gap-2"><Truck className="h-4 w-4" /> Fraktinställningar</Label>
              <p className="text-xs text-gray-500 mb-3">ByggFynd beräknar fraktkostnaden baserat på vikt och mått</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div><Label htmlFor="weight_kg">Vikt (kg)</Label><Input id="weight_kg" type="number" step="0.1" value={formData.weight_kg} onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })} placeholder="t.ex. 2.5" className="mt-1" /></div>
                <div><Label htmlFor="length_cm">Längd (cm)</Label><Input id="length_cm" type="number" value={formData.length_cm} onChange={(e) => setFormData({ ...formData, length_cm: e.target.value })} placeholder="t.ex. 50" className="mt-1" /></div>
                <div><Label htmlFor="width_cm">Bredd (cm)</Label><Input id="width_cm" type="number" value={formData.width_cm} onChange={(e) => setFormData({ ...formData, width_cm: e.target.value })} placeholder="t.ex. 30" className="mt-1" /></div>
                <div><Label htmlFor="height_cm">Höjd (cm)</Label><Input id="height_cm" type="number" value={formData.height_cm} onChange={(e) => setFormData({ ...formData, height_cm: e.target.value })} placeholder="t.ex. 20" className="mt-1" /></div>
              </div>
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-start gap-3">
                  <input type="checkbox" id="requiresShippingQuote" checked={requiresShippingQuote} onChange={(e) => setRequiresShippingQuote(e.target.checked)} className="h-4 w-4 mt-1 rounded border-gray-300 text-[#FF8A00] focus:ring-[#FF8A00]" />
                  <div>
                    <Label htmlFor="requiresShippingQuote" className="cursor-pointer font-semibold text-yellow-800">Varan är tung/skrymmande – kontakta varuhuset för fraktförfrågan</Label>
                    <p className="text-xs text-yellow-700 mt-1">Frakten beräknas inte automatiskt. Kunden ser istället ett meddelande i kassan.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={() => setViewMode('preview')} className="w-full sm:w-auto"><Eye className="h-4 w-4 mr-2" />Förhandsgranska</Button>
            <div className="flex gap-2 w-full sm:w-auto">
              <DialogClose asChild><Button type="button" variant="outline">Avbryt</Button></DialogClose>
              <Button type="submit" disabled={loading} className="bg-[#FF8A00] hover:bg-[#e67c00]">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {product ? 'Uppdatera annons' : 'Lägg till annons'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      )}
    </DialogContent>
  );
};
